import { useTranslation } from 'react-i18next';

import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';

import {
  BarraApilada,
  formatPorcentaje,
  TarjetaInforme,
} from '#components/mobile/informes/comunes';
import { marcaCambio } from '#components/mobile/informes/gastosRosa';
import { useColoresCategorias } from '#components/mobile/informes/useColoresCategorias';
import type { PropsWidget } from '#components/mobile/inicio/comunes';
import { color } from '#components/mobile/ui/tokens';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { useNavigate } from '#hooks/useNavigate';

import { FilaDato } from './FilaDato';
import { useGastoDelMes } from './useGastoDelMes';

/**
 * «En qué se va»: lo gastado este mes, la barra de colores por categoría y,
 * en grande, las cuatro mayores con su ▲▼ frente al mes anterior a igual día.
 */
export function TarjetaSeVa({ tamano }: PropsWidget) {
  const { t } = useTranslation();
  const format = useFormat();
  const locale = useLocale();
  const navigate = useNavigate();
  const fmt = (v: number) => format(v, 'financial-no-decimals');
  const { mes, categorias, analisis, isLoading } = useGastoDelMes();
  const filas = analisis.filas;
  const { colorDe } = useColoresCategorias(filas.map(f => f.categoria));
  const nombreDe = (id: string | null) =>
    id == null
      ? t('Uncategorized')
      : (categorias.get(id)?.nombre ?? t('Unknown'));
  const mayor = filas[0];
  const total = analisis.total;
  const nombreMes = monthUtils.format(mes, 'MMMM', locale);

  return (
    <TarjetaInforme
      enRejilla
      data-testid="tarjeta-se-va"
      titulo={t('Where it goes')}
      valor={isLoading ? '…' : fmt(total)}
      frase={
        isLoading || tamano === 'compacto'
          ? ''
          : !mayor || total === 0
            ? t('No spending yet in {{month}}.', { month: nombreMes })
            : t('Mostly {{category}} ({{pct}}).', {
                category: nombreDe(mayor.categoria),
                pct: formatPorcentaje(mayor.fraccion, false),
              })
      }
      onPress={() => void navigate('/reports/gasto')}
    >
      {!isLoading && total > 0 && tamano !== 'compacto' && (
        <View style={{ gap: 8 }}>
          <BarraApilada
            total={total}
            alto={12}
            segmentos={filas.map(f => ({
              clave: f.clave,
              valor: f.importe,
              color: colorDe(f.categoria),
              nombre: nombreDe(f.categoria),
            }))}
          />
          {tamano === 'grande' && (
            <View>
              {filas.slice(0, 4).map(f => {
                const marca = marcaCambio(f);
                return (
                  <FilaDato
                    key={f.clave}
                    colorPunto={colorDe(f.categoria)}
                    nombre={nombreDe(f.categoria)}
                    nota={
                      marca == null
                        ? undefined
                        : marca.tipo === 'nuevo'
                          ? t('new')
                          : `${marca.fraccion >= 0 ? '▲' : '▼'} ${formatPorcentaje(
                              Math.abs(marca.fraccion),
                              false,
                            )}`
                    }
                    colorNota={
                      marca?.tipo === 'cambio' && marca.fraccion < 0
                        ? color.ok
                        : color.warn
                    }
                    valor={fmt(f.importe)}
                  />
                );
              })}
            </View>
          )}
        </View>
      )}
    </TarjetaInforme>
  );
}
